import React, { useState } from 'react';
import { Title, useStore, useNotify, Confirm, useGetIdentity } from 'react-admin';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import type { Partner } from '@src/types';
import { getEnv } from '@src/apis/shared';
import { useTitle } from '@src/hooks/useTitle';
import { ThemeDeploySelector, PipelineResult } from '@src/services/storefront';
import type { Pipeline } from '@src/services/storefront';

import { useThemeVersions } from './useThemeVersions';
import { useThemeDeploy, isTerminal } from './useThemeDeploy';
import { isTroveInternalEmail } from './isTroveInternalEmail';

const BITBUCKET_LIQUID_PIPELINES = 'https://bitbucket.org/troverecommerce/liquid/pipelines/results';

/** Format an ISO timestamp for display; fall back to the raw string if it doesn't parse. */
const formatDateTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

/**
 * Map a run to a semantic MUI chip. A run with no result yet shows the spinner ONLY while genuinely
 * non-terminal; a terminal run without a result (e.g. COMPLETED before Bitbucket assigns one) shows
 * a neutral "finished" chip, not a spinner. Otherwise: success = secondary (slate ink — green is
 * reserved for the "Live" badge); fail/error = error; stopped = warning; unknown = neutral.
 * NOTE: slateInkTheme does not override info/warning/error, so those chips fall back to MUI defaults.
 * The `default: … satisfies never` gives compile-time exhaustiveness — a new contract result value
 * fails to compile here rather than silently falling through to a stuck-looking spinner.
 */
const RunStateChip = ({ pipeline }: { pipeline: Pipeline }): React.ReactElement => {
  const { result } = pipeline;
  if (result === undefined) {
    return isTerminal(pipeline) ? (
      <Chip color="default" size="small" label="Deploy finished (status unknown)" />
    ) : (
      <Chip
        color="info"
        size="small"
        icon={<CircularProgress size={14} color="inherit" />}
        label={`Deploy ${pipeline.state.toLowerCase().replace(/_/g, ' ')}`}
      />
    );
  }
  switch (result) {
    case PipelineResult.Successful:
      return <Chip color="secondary" size="small" label="Deploy succeeded" />;
    case PipelineResult.Failed:
    case PipelineResult.Error:
      return <Chip color="error" size="small" label={`Deploy ${result.toLowerCase()}`} />;
    case PipelineResult.Stopped:
      return <Chip color="warning" size="small" label="Deploy stopped" />;
    case PipelineResult.Unknown:
      return <Chip color="default" size="small" label="Deploy finished (status unknown)" />;
    default:
      throw new Error(`Unsupported pipeline result: ${result satisfies never}`);
  }
};

/**
 * A single version "slot" card. The live (production) slot is accented so production is
 * visually distinct from the preview at a glance — the Active-vs-Draft distinction the feature
 * is meant to convey. An absent version reads as "Not deployed yet" rather than a bare dash.
 */
const VersionPanel = ({
  title,
  version,
  badgeLabel,
  badgeColor,
  live = false,
  children,
}: {
  title: string;
  version: string | undefined;
  badgeLabel: string;
  badgeColor: 'success' | 'default';
  live?: boolean;
  children?: React.ReactNode;
}) => (
  <Card
    variant="outlined"
    sx={{
      flex: 1,
      minWidth: 240,
      // Dark ink accent for the live panel (green stays only on the "Live" badge).
      borderColor: live ? 'secondary.main' : 'divider',
      borderWidth: live ? 2 : 1,
    }}
  >
    <CardContent>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
        <Chip
          size="small"
          color={badgeColor}
          label={badgeLabel}
          variant={live ? 'filled' : 'outlined'}
        />
      </Stack>
      {version ? (
        <Typography variant="h5" sx={{ mt: 1, fontVariantNumeric: 'tabular-nums' }}>
          {version}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          Not deployed yet
        </Typography>
      )}
      {children}
    </CardContent>
  </Card>
);

/**
 * Storefront theme-versions page. Reads the current partner's versions via the theme-versions
 * client service and renders the live (production) and preview versions in two distinct panels,
 * plus deploy controls that trigger a liquid pipeline and poll it to a terminal state.
 *
 * `regen` is the version currently live on the partner's production theme; `regenPreview` is the
 * version currently live on the preview theme; `latest` is the newest version available in source
 * control (what a regenerate would stage). The values are produced backend-side and published as a
 * snapshot the page reads — an unbuilt or unreadable partner comes back empty and reads as "not yet
 * published". The read can fail (the production read can 500), so `error` is an
 * explicit error state rather than a silent empty panel. Deploy controls show only when the read
 * succeeded, so an unconfigured/unreadable partner shows no dead buttons.
 */
export const ThemeVersionsPage = () => {
  const { title } = useTitle('Storefront Theme');
  const [currentPartner] = useStore<Partner | null>('currentPartner', null);
  const partnerCode = currentPartner?.code;
  const notify = useNotify();

  // The Bitbucket run link is only meaningful for internal users; external partner users have no
  // Bitbucket access, so it's gated on a Trove-internal email domain.
  const { identity } = useGetIdentity();
  const isInternalUser = isTroveInternalEmail(identity?.email);

  const { data, isLoading, error, refetch } = useThemeVersions(partnerCode);
  const { pipeline, isDeploying, deploy, error: deployError } = useThemeDeploy(partnerCode, refetch);

  // Only show the full-panel spinner on the first load. A background refetch (e.g. the automatic
  // post-deploy refresh) keeps the current panels + run status visible instead of collapsing them.
  const isInitialLoading = isLoading && !data;

  const [confirmOpen, setConfirmOpen] = useState(false);
  // Production deploys only run in the production environment (the backend enforces this too);
  // off-prod the button is disabled-with-tooltip rather than hidden, so the action is discoverable.
  const isProd = getEnv('YERDLE_ENV') === 'production';

  const hasNoVersions =
    !!data && !data.latest && !data.regenPreview && !data.regen && !data.updatedAt;

  // The preview is behind source control when a newer `latest` exists — a regenerate would stage it.
  const previewBehindLatest = !!data?.latest && data.latest !== data.regenPreview;

  const runDeploy = async (selector: ThemeDeploySelector) => {
    try {
      await deploy(selector);
      notify('Deploy triggered.', { type: 'info' });
    } catch (caught) {
      const status =
        (caught as { response?: { status?: number } })?.response?.status ??
        (caught as { status?: number })?.status;
      console.error('ThemeVersionsPage deploy failed:', {
        partnerCode,
        selector,
        status,
        error: caught instanceof Error ? caught.message : String(caught),
      });
      if (status === 409) {
        notify('A deploy is already running for this partner.', { type: 'warning' });
      } else {
        notify('Failed to trigger the deploy.', { type: 'error' });
      }
    }
  };

  const renderBody = () => {
    if (!partnerCode) {
      return <Alert severity="info">Select a partner to view its storefront theme versions.</Alert>;
    }
    if (isInitialLoading) {
      return <CircularProgress aria-label="Loading theme versions" />;
    }
    if (error) {
      return (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Unable to load theme versions.
        </Alert>
      );
    }

    return (
      <Stack spacing={2}>
        {hasNoVersions ? (
          <Alert severity="info">This partner&apos;s storefront theme has not been published yet.</Alert>
        ) : (
          <>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="stretch">
              <VersionPanel
                title="Live on production"
                version={data?.regen}
                badgeLabel="Live"
                badgeColor="success"
                live
              />
              <VersionPanel
                title="Preview"
                version={data?.regenPreview}
                badgeLabel="Preview"
                badgeColor="default"
              >
                {previewBehindLatest ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {`Newer version ${data?.latest} available — regenerate to stage it.`}
                  </Typography>
                ) : undefined}
              </VersionPanel>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {'Latest available in source control: '}
              <Typography component="span" variant="body2" color="text.primary">
                {data?.latest ?? '—'}
              </Typography>
              {data?.updatedAt ? ` · Last updated ${formatDateTime(data.updatedAt)}` : ''}
            </Typography>
          </>
        )}

        <Divider />

        {deployError ? (
          <Alert severity="warning">
            Lost track of the deploy status — it may still be running. Use Refresh to check.
          </Alert>
        ) : undefined}

        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <Button
            variant="outlined"
            disabled={isDeploying}
            startIcon={isDeploying ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => runDeploy(ThemeDeploySelector.BumpPartnerLiquidVersion)}
          >
            Regenerate preview
          </Button>

          <Tooltip title={isProd ? '' : 'Production deploys are only available in the production environment.'}>
            {/* span wrapper so the tooltip still shows while the button is disabled */}
            <span>
              <Button
                variant="contained"
                color="error"
                disabled={isDeploying || !isProd}
                startIcon={isDeploying ? <CircularProgress size={16} color="inherit" /> : undefined}
                onClick={() => setConfirmOpen(true)}
              >
                Deploy to production
              </Button>
            </span>
          </Tooltip>

          <Button variant="text" onClick={() => refetch()} disabled={isDeploying}>
            Refresh
          </Button>

          {pipeline ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <RunStateChip pipeline={pipeline} />
              {pipeline.buildNumber != null && isInternalUser ? (
                <Link
                  href={`${BITBUCKET_LIQUID_PIPELINES}/${pipeline.buildNumber}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                >
                  {`View run #${pipeline.buildNumber}`}
                </Link>
              ) : undefined}
            </Stack>
          ) : undefined}
        </Stack>

        <Confirm
          isOpen={confirmOpen}
          title="Deploy to production?"
          content="This promotes the current preview theme to the live storefront for this partner and affects production."
          onConfirm={() => {
            setConfirmOpen(false);
            void runDeploy(ThemeDeploySelector.DeployPartnerToProd);
          }}
          onClose={() => setConfirmOpen(false)}
        />
      </Stack>
    );
  };

  return (
    <Card>
      <Title title={title} />
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Storefront Theme Versions
        </Typography>
        {renderBody()}
      </CardContent>
    </Card>
  );
};
